import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { RouteData } from 'src/app/core/models/route-data';
import { SmartRedirectGuard } from 'src/app/core/guards/smart-redirect.guard';
import { UserContextResolverService } from 'src/app/core/guards/user-context-resolver.service';
import { LoginComponent } from 'src/components/login/login.component';
import { LoginHelpComponent } from 'src/components/login/login-help/login-help.component';
import { AgencyLoginComponent } from 'src/components/login/agency-login/agency-login.component';
import { AuthenticateComponent } from 'src/components/authenticate/authenticate.component';
import { SsoLoginComponent } from 'src/components/login/sso-login/sso-login.component';
import { SsoExpiredComponent } from 'src/components/sso/sso-expired.component';
import { SsoInviteComponent } from 'src/components/sso-invite/sso-invite.component';
import { SsoProducerNotFoundComponent } from 'src/components/sso-producer-not-found/sso-producer-not-found.component';
import { ForgotPasswordComponent } from 'src/components/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from 'src/components/reset-password/reset-password.component';
import { ExpiredPasswordComponent } from 'src/components/expired-password/expired-password.component';
import { SignInHelpWorkflowComponent } from 'src/components/sign-in-help-workflow/sign-in-help-workflow.component';
import { EmptyComponent } from 'src/app/empty.component';
import { RedirectComponent } from 'src/components/redirect/redirect.component';
import { ANGULAR_REDIRECT_ROUTES } from './core/routing/angular-redirects.routes';
import { NETWORK_ROUTES } from './core/routing/network.routes';
import { DASHBOARD_ROUTES } from './core/routing/dashboard.routes';
import { WORKFLOW_PUBLIC_ROUTES, WORKFLOW_ROUTES } from './core/routing/workflow.routes';
import { PROFILE_CONFIG_ROUTES } from './core/routing/profile-config.routes';
import { SIGNUP_INVITE_ROUTES } from './core/routing/signup-invite.routes';
import { MISC_PUBLIC_ROUTES, MISC_ROUTES } from './core/routing/misc.routes';
import { ACCOUNT_MANAGEMENT_PUBLIC_ROUTES, ACCOUNT_MANAGEMENT_ROUTES } from './core/routing/account-management.routes';
import { REPORTING_ROUTES } from './core/routing/reporting.routes';
import { REMAINING_ROUTES } from './core/routing/remaining.routes';
import { FeatureFlagDashboardComponent } from 'src/components/feature-flag-dashboard/feature-flag-dashboard.component';
import { RoutingPerformanceDashboardComponent } from 'src/components/routing-performance-dashboard/routing-performance-dashboard.component';
import { ErrorComponent } from 'src/components/error/error.component';

/**
 * Public routes (login, SSO, password reset) are at root level.
 * All protected routes are children of a parent route with UserContextResolverService,
 * which loads user context once per session so all child routes inherit it immediately.
 */
export const appRoutes: Routes = [
    {
        path: '',
        component: LoginComponent,
        canActivate: [EnhancedAuthGuard],
        data: { unauthenticated: true } satisfies RouteData
    },
    {
        path: 'agencyLogin',
        component: AgencyLoginComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'agencyLogin',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'authenticate',
        component: AuthenticateComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'authenticate',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'ssoLogin',
        component: SsoLoginComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'ssoLogin',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'ssoExpired',
        component: SsoExpiredComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'ssoExpired',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'ssoInvite',
        component: SsoInviteComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'ssoInvite',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'ssoProducerNotFound',
        component: SsoProducerNotFoundComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'ssoProducerNotFound',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'login',
        component: LoginComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'login',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'login/help',
        component: LoginHelpComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'login.help',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'forgotPassword',
        component: ForgotPasswordComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'forgotPassword',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'resetPassword',
        component: ResetPasswordComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'resetPassword',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'expiredPassword',
        component: ExpiredPasswordComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'expiredPassword',
            unauthenticated: true
        } satisfies RouteData
    },
    {
        path: 'signInHelpWorkflow',
        component: SignInHelpWorkflowComponent,
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'signInHelpWorkflow',
            unauthenticated: true
        } satisfies RouteData
    },

    // Signup/invite routes are unauthenticated and must remain at root level,
    // not inside the protected parent.
    ...SIGNUP_INVITE_ROUTES,

    // Public routes with named outlets must also stay at root level so the guard
    // can evaluate unauthenticated: true before user context loads.
    ...ACCOUNT_MANAGEMENT_PUBLIC_ROUTES,
    ...WORKFLOW_PUBLIC_ROUTES,
    ...MISC_PUBLIC_ROUTES,

    {
        path: '',
        canActivate: [EnhancedAuthGuard],
        resolve: {
            userContext: UserContextResolverService  // Runs ONCE per session; all children inherit resolved context
        },
        children: [
            {
                path: 'redirect',
                component: RedirectComponent,
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'redirect',
                component: EmptyComponent,
                outlet: 'header'
            },
            {
                path: 'redirect',
                component: EmptyComponent,
                outlet: 'footer'
            },
            {
                path: 'redirect',
                component: EmptyComponent,
                outlet: 'sideRail'
            },

            ...NETWORK_ROUTES,
            ...DASHBOARD_ROUTES,
            ...WORKFLOW_ROUTES,
            ...PROFILE_CONFIG_ROUTES,
            ...MISC_ROUTES,
            ...ACCOUNT_MANAGEMENT_ROUTES,
            ...REPORTING_ROUTES,
            ...REMAINING_ROUTES,

            ...ANGULAR_REDIRECT_ROUTES,

            {
                path: 'smart-dashboard',
                canActivate: [SmartRedirectGuard],
                data: {
                    redirectType: 'dashboard'
                } satisfies RouteData
            },
            {
                path: 'smart-network',
                canActivate: [SmartRedirectGuard],
                data: {
                    redirectType: 'network'
                } satisfies RouteData
            },

            {
                path: 'dev/feature-flags',
                component: FeatureFlagDashboardComponent
            },
            {
                path: 'dev/performance',
                component: RoutingPerformanceDashboardComponent
            }
        ]
    },

    {
        path: 'error',
        component: ErrorComponent,
        data: {
            helpRouteName: 'error',
            unauthenticated: true
        } satisfies RouteData
    },

    { path: '**', redirectTo: '/error' }  // must be last
];
